/**
 * quickExpr — 빠른 조건 편집(개념·라벨·난이도)을 **같은 식 모델** 위에서 한다(설계 FE-20260912 §W01·§W04).
 *
 * 빠른 편집은 언제나 **고른 묶음 하나**만 고친다. 묶음이 둘 이상인 식을 「난이도 3·5」 하나로 평탄화하지 않는다.
 * 같은 축의 항이 둘 이상이면 고른 항만 고친다. termIndex 를 생략한 기존 호출은 첫 항을 고친다.
 * 값을 모두 빼면 그 항을 뺀다 — 난이도의 「제한 없음」은 항을 없애는 것이다. 1~5 전부 켠 것과 다르다
 * (전부 켜면 「난이도가 매겨진 문제만」이 된다).
 *
 * 마지막 항까지 빠진 묶음은 **빈 묶음으로 남긴다**(미완성 초안). 저절로 제목 줄로 바꾸거나 전체 검색으로 가지 않는다 —
 * 빈 묶음은 식 검사(normalizeExpr)가 「묶음에 조건이 없습니다」로 막는다.
 *
 * 모든 함수는 새 식을 돌려준다(원본을 고치지 않는다). 식이 null(제목 줄)이면 묶음 하나짜리 새 식에서 시작한다.
 *
 * 개념 범위 조건(isRangeTerm — 개념 「제외」 · 범위에서 펼친 「하나라도」)은 식에서는 개념 항이지만 빠른 편집의 「개념」이 아니다.
 * 매스컨셉 줄이 고치지 않고 「그 밖」으로 보인다(오너 2026-09-15 「범위 기능으로 별도로」).
 */
import {
  EXPR_VERSION, FIELDS, RANGE_FIELD, describeTerm, isRangeTerm,
} from './syllabusQuery';

export const QUICK_FIELDS = ['concept', 'label', 'difficulty'];

const cloneExpr = (expr) => ({
  version: expr?.version ?? EXPR_VERSION,
  groups: (Array.isArray(expr?.groups) ? expr.groups : []).map((g) => ({
    terms: (Array.isArray(g?.terms) ? g.terms : []).map((t) => ({ ...t, values: [...(t.values || [])] })),
  })),
});

/** 조건을 처음 만드는 줄 — 빈 묶음 하나(값을 고르기 전까지는 검색·저장이 막힌다) */
export const newExpr = () => ({ version: EXPR_VERSION, groups: [{ terms: [] }] });

export const groupCount = (expr) => (Array.isArray(expr?.groups) ? expr.groups.length : 0);
export const isComplex = (expr) => groupCount(expr) > 1;
export const clampGroup = (expr, g) => Math.min(Math.max(0, Number(g) || 0), Math.max(0, groupCount(expr) - 1));

/** 묶음 이름 — 화면에서 「묶음 A」「묶음 B」로 부른다(번호보다 덜 헷갈린다) */
export const groupName = (g) => `묶음 ${String.fromCharCode(65 + g)}`;

const termsOf = (expr, g) => {
  const grp = Array.isArray(expr?.groups) ? expr.groups[g] : null;
  return Array.isArray(grp?.terms) ? grp.terms : [];
};

/** 빠른 편집이 보는 항의 축 — 개념 범위 조건은 「개념」이 아니라 따로(RANGE_FIELD) */
const quickFieldOf = (t) => (isRangeTerm(t) ? RANGE_FIELD : t?.field);

/** 그 묶음에서 그 축의 항 번호들 */
export const termIndexes = (expr, g, field) => termsOf(expr, g).map((t, j) => (quickFieldOf(t) === field ? j : -1)).filter((j) => j >= 0);

/** 빠른 편집이 고칠 항 — 그 축의 첫 항(없으면 index -1). 같은 축이 둘 이상이면 duplicate */
export function quickTarget(expr, g, field, termIndex) {
  const idx = termIndexes(expr, g, field);
  const index = termIndex == null ? (idx[0] ?? -1) : (idx.includes(termIndex) ? termIndex : -1);
  return { index, duplicate: idx.length > 1 };
}

/** 빠른 편집이 고칠 항 그 자체(없으면 null) */
export function quickTerm(expr, g, field, termIndex) {
  const { index } = quickTarget(expr, g, field, termIndex);
  return index < 0 ? null : termsOf(expr, g)[index];
}

export const quickValues = (expr, g, field, termIndex) => quickTerm(expr, g, field, termIndex)?.values || [];

/** 빠른 편집이 맡지 않는 항 번호 — 다른 축, 그리고 같은 축의 두 번째 항부터(요약 칩으로만 보인다) */
export function otherTermIndexes(expr, g, targets = {}) {
  const firsts = new Set(QUICK_FIELDS.map((f) => quickTarget(expr, g, f, targets[f]).index).filter((j) => j >= 0));
  return termsOf(expr, g).map((_, j) => j).filter((j) => !firsts.has(j));
}

function withGroup(expr, g, fn) {
  const base = expr ? cloneExpr(expr) : newExpr();
  if (!base.groups.length) base.groups.push({ terms: [] });
  const gi = clampGroup(base, g);
  base.groups[gi] = { terms: fn(base.groups[gi].terms) };
  return base;
}

const defaultMatch = (field) => (FIELDS[field] ? FIELDS[field].matches[0] : 'any');

/**
 * 그 축의 첫 항 값을 바꾼다. 값이 비면 항을 뺀다. 항이 없으면 묶음 끝에 새로 만든다(방식은 그 축의 첫 허용값).
 * source(순번 범위 유래)는 **새로 준 경우에만** 붙인다 — 손으로 고친 값에 옛 유래를 남기면 사실이 아니게 된다.
 * match 를 주지 않으면 기존 방식을 유지한다(팝오버를 열었다 닫았다고 방식이 바뀌지 않는다, 설계 §W02).
 */
export function setFieldValues(expr, g, field, values, { match, source, termIndex } = {}) {
  const vals = Array.isArray(values) ? values : [];
  // 표시 이후 삭제·축 변경으로 대상이 사라졌다면 다른 항에 잘못 적용하지 않는다.
  if (termIndex != null && quickTarget(expr, g, field, termIndex).index < 0) return expr;
  return withGroup(expr, g, (terms) => {
    const j = termIndex ?? terms.findIndex((t) => quickFieldOf(t) === field);
    if (!vals.length) return j < 0 ? terms : terms.filter((_, k) => k !== j);
    const next = { field, match: match || (j < 0 ? defaultMatch(field) : terms[j].match), values: [...vals] };
    if (source) next.source = { ...source };
    if (j < 0) return [...terms, next];
    return terms.map((t, k) => (k === j ? next : t));
  });
}

/**
 * 방식만 바꾼 항 — 값·유래는 그대로. 다만 매스컨셉 조건을 「하나라도」로 돌리면 순번 범위 유래를 뗀다:
 * 남기면 식이 개념 범위 조건(유래 붙은 하나라도, isRangeTerm)으로 읽혀 누른 줄이 개념 범위 줄로 옮겨 간다
 */
export function withMatch(t, match) {
  if (t?.field === 'concept' && match === 'any' && t.source) {
    const { source, ...rest } = t;
    return { ...rest, match };
  }
  return { ...t, match };
}

/** 그 축의 첫 항 방식만 바꾼다(값·유래는 그대로 — withMatch). 항이 없으면 그대로 */
export function setFieldMatch(expr, g, field, match, termIndex) {
  if (!FIELDS[field]?.matches.includes(match)) return expr;
  if (termIndex != null && quickTarget(expr, g, field, termIndex).index < 0) return expr;
  return withGroup(expr, g, (terms) => {
    const j = termIndex ?? terms.findIndex((t) => quickFieldOf(t) === field);
    return j < 0 ? terms : terms.map((t, k) => (k === j ? withMatch(t, match) : t));
  });
}

/** 난이도 한 칸 켜고 끄기 — 마지막 칸을 끄면 항이 빠진다(= 제한 없음). 항이 없으면 새로 만든다 */
export function toggleDifficulty(expr, g, n, termIndex) {
  const cur = quickValues(expr, g, 'difficulty', termIndex);
  const next = cur.includes(n) ? cur.filter((v) => v !== n) : [...cur, n].sort((a, b) => a - b);
  return setFieldValues(expr, g, 'difficulty', next, { termIndex });
}

/** 난이도 「제한 없음」 — 난이도 항을 뺀다 */
export const clearDifficulty = (expr, g, termIndex) => setFieldValues(expr, g, 'difficulty', [], { termIndex });

/** 묶음 하나를 뺀다 — 마지막 묶음이면 빈 묶음 하나로 남긴다(미완성 초안) */
export function removeGroup(expr, g) {
  const base = cloneExpr(expr);
  if (base.groups.length <= 1) return newExpr();
  base.groups.splice(clampGroup(base, g), 1);
  return base;
}

/** 빈 묶음이 있는가 — 검색·저장이 막히는 「조건을 하나 이상 고르세요」 상태 */
export const hasEmptyGroup = (expr) => (Array.isArray(expr?.groups) ? expr.groups : []).some((grp) => !(grp?.terms || []).length);

// ── 요약(나무의 두 번째 줄 · 묶음 고르기 칩) ──

/** 난이도 값 — 「2·3」 */
export const difficultyText = (values) => [...(values || [])].sort((a, b) => a - b).join('·');

function shortTerm(t, names) {
  const vals = Array.isArray(t?.values) ? t.values : [];
  if (isRangeTerm(t) && t.source) return describeTerm(t, names); // 「순번 100 이상 제외」 — 개념 이름을 늘어놓지 않는다
  if (t?.field === 'concept') {
    const ns = vals.map((v) => (names.concept && names.concept(v)) || `#${v}`);
    const head = ns.slice(0, 2).join(', ');
    const body = ns.length > 2 ? `${head} 외 ${ns.length - 2}개` : head;
    if (t.match === 'all' && ns.length > 1) return `${body} 모두`;
    if (t.match === 'exact') return `${body}(정확히)`;
    if (t.match === 'none') return `${body} 제외`;
    return body;
  }
  if (t?.field === 'difficulty') return `난이도 ${difficultyText(vals)}`;
  if (t?.field === 'label') return `라벨 ${vals.length}개`;
  return describeTerm(t, names);
}

/** 묶음 하나의 짧은 요약 — 「다항식의 전개 · 난이도 3」 */
export function summarizeGroup(expr, g, names = {}) {
  const terms = termsOf(expr, g);
  if (!terms.length) return '조건 없음';
  return terms.map((t) => shortTerm(t, names)).join(' · ');
}

/**
 * 나무 두 번째 줄의 요약 — 묶음이 하나면 그 요약, 둘 이상이면 「복합 조건 · 묶음 2개」.
 * 요약은 받아 둔 식에서 만든다 — 줄마다 건수를 세려고 검색을 쏘지 않는다(설계 §W01).
 */
export function summarizeExpr(expr, names = {}) {
  if (expr == null) return '';
  const n = groupCount(expr);
  if (n > 1) return `복합 조건 · 묶음 ${n}개`;
  return summarizeGroup(expr, 0, names);
}

/** 행 찾기는 화면용 축약 요약과 다르다. 모든 묶음·항의 값과 이미 해석할 수 있는 이름을 빠짐없이 포함한다. */
export function searchTextOfExpr(expr, names = {}) {
  return (Array.isArray(expr?.groups) ? expr.groups : []).flatMap((group) => (
    (Array.isArray(group?.terms) ? group.terms : []).map((term) => {
      const values = Array.isArray(term?.values) ? term.values : [];
      const lookup = term?.field === 'concept' ? names.concept : (term?.field === 'label' ? names.label : null);
      const valueText = values.map((value) => [value, typeof lookup === 'function' ? lookup(value) : ''].filter((v) => v != null).join(' '));
      return [FIELDS[term?.field]?.label || '', ...valueText].join(' ');
    })
  )).join(' ');
}
